/**
 * Shared WebSocket connection for the WhatsApp Inbox (FE-04).
 *
 * Reuses the existing /api/ws infra (same endpoint the app-wide notification
 * badge connects to via hooks/useWebSocket.ts) but opens its own connection
 * scoped to the Inbox feature, so a single WhatsAppSocketProvider — mounted
 * once at the top of the Inbox page — fans wa_message_new/wa_status_update
 * events out to every consumer (conversation list, open thread, unread
 * badges) without each of them opening a redundant socket.
 *
 * Unlike useWebSocket.ts's fixed 5s reconnect, this backs off exponentially
 * and flips into "degraded" mode after a sustained outage so consumers can
 * fall back to polling rather than just sitting silently stale.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode,
} from 'react';

export type WhatsAppWsEvent =
  | { type: 'wa_message_new'; conversation_uuid: string }
  | { type: 'wa_status_update'; conversation_uuid: string; wa_message_id: string; status: string };

type Listener = (event: WhatsAppWsEvent) => void;

interface WhatsAppSocketContextValue {
  subscribe: (listener: Listener) => () => void;
  connected: boolean;
  /** True once reconnection has been failing long enough that consumers
   * should fall back to polling rather than trusting the live feed. */
  degraded: boolean;
}

const WhatsAppSocketContext = createContext<WhatsAppSocketContextValue | null>(null);

const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30_000;
const DEGRADED_AFTER_MS = 15_000;

function getWsBase(): string {
  const socketUrl = import.meta.env.VITE_SOCKET_URL as string | undefined;
  if (socketUrl) return socketUrl;
  const apiUrl = import.meta.env.VITE_API_URL as string | undefined;
  if (apiUrl) return apiUrl.replace(/^http/, 'ws').replace(/\/api$/, '');
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://localhost:6002`;
}

function getToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)crm_auth_token=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function isWhatsAppEvent(msg: any): msg is WhatsAppWsEvent {
  return msg && (msg.type === 'wa_message_new' || msg.type === 'wa_status_update');
}

export const WhatsAppSocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const enabled = import.meta.env.VITE_SOCKET_ENABLED !== 'false';

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const degradedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptRef = useRef(0);
  const listenersRef = useRef<Set<Listener>>(new Set());
  const mountedRef = useRef(true);

  const [connected, setConnected] = useState(false);
  const [degraded, setDegraded] = useState(!enabled);

  const subscribe = useCallback((listener: Listener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const armDegradedCheck = useCallback(() => {
    if (degradedTimerRef.current) clearTimeout(degradedTimerRef.current);
    degradedTimerRef.current = setTimeout(() => {
      if (mountedRef.current && !wsRef.current) setDegraded(true);
    }, DEGRADED_AFTER_MS);
  }, []);

  const connect = useCallback(() => {
    if (!mountedRef.current || !enabled) return;
    const token = getToken();
    if (!token) return;

    const url = `${getWsBase()}/api/ws?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    // Keep-alive: nginx drops a proxied socket after 60 s with nothing from the server,
    // so we ping every 25 s (the server answers "pong"). Without it the Inbox
    // reconnected every minute and missed messages arriving in the gap.
    let pingTimer: ReturnType<typeof setInterval> | null = null;

    ws.onopen = () => {
      pingTimer = setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send('ping'); }, 25_000);
      attemptRef.current = 0;
      setConnected(true);
      setDegraded(false);
      if (degradedTimerRef.current) clearTimeout(degradedTimerRef.current);
    };

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (isWhatsAppEvent(msg)) {
          listenersRef.current.forEach((listener) => listener(msg));
        }
        // Other message types (init/notif_inc/claims_*) belong to
        // hooks/useWebSocket.ts's own connection — ignored here.
      } catch {
        /* ignore malformed frame */
      }
    };

    ws.onclose = () => {
      if (pingTimer) clearInterval(pingTimer);
      wsRef.current = null;
      setConnected(false);
      if (!mountedRef.current) return;
      attemptRef.current += 1;
      const backoff = Math.min(INITIAL_BACKOFF_MS * 2 ** attemptRef.current, MAX_BACKOFF_MS);
      armDegradedCheck();
      reconnectRef.current = setTimeout(connect, backoff);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [enabled, armDegradedCheck]);

  useEffect(() => {
    mountedRef.current = true;
    armDegradedCheck();
    connect();
    return () => {
      mountedRef.current = false;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (degradedTimerRef.current) clearTimeout(degradedTimerRef.current);
      wsRef.current?.close();
    };
  }, [connect, armDegradedCheck]);

  return (
    <WhatsAppSocketContext.Provider value={{ subscribe, connected, degraded }}>
      {children}
    </WhatsAppSocketContext.Provider>
  );
};

export function useWhatsAppSocket(): WhatsAppSocketContextValue {
  const ctx = useContext(WhatsAppSocketContext);
  if (!ctx) {
    throw new Error('useWhatsAppSocket must be used within a WhatsAppSocketProvider');
  }
  return ctx;
}

/**
 * Subscribe a callback to live WhatsApp events for the calling component's
 * lifetime. Returns `degraded` so the caller can fall back to polling
 * (see useConversationList.ts / useConversationThread.ts) when the socket
 * has been down long enough that the live feed can't be trusted.
 */
export function useWhatsAppSocketEvents(onEvent: Listener): { degraded: boolean; connected: boolean } {
  const { subscribe, degraded, connected } = useWhatsAppSocket();
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => subscribe((event) => onEventRef.current(event)), [subscribe]);

  return { degraded, connected };
}
